import { parseDatamapCsv } from "features/DataImport/components/DatamapEditor/utils/datamapCsv"
import { validateDatamap } from "features/DataImport/components/DatamapEditor/utils/validateDatamap"

export const NOT_UTF8_NOTE =
  'This file is not UTF-8: Excel saved it as plain "CSV" instead of "CSV UTF-8", so dashes, curly quotes and accented letters would not match. It has been read as Windows-1252. Check those characters below, then save to upload it as UTF-8.'

export const STORED_NOT_UTF8_NOTE =
  'This datamap is stored in Excel\'s plain "CSV" format, not UTF-8, so dashes, curly quotes and accented letters may not match the data. It has been read as Windows-1252. Check those characters below, then save to store it as UTF-8.'

/** A stored datamap with empty rows: the engine reads an empty row as an invalid type, or a leading one as the header. */
export const storedBlankRowsNote = (blankRows: number): string =>
  `The stored datamap has ${blankRows} empty row${blankRows === 1 ? "" : "s"}, which the import can't read. Saving here removes ${blankRows === 1 ? "it" : "them"}.`

export const STORED_LOOSE_HEADER_NOTE =
  "The stored datamap's header row is not exactly LABEL,TYPE,VARIABLE,VALUES, so the import can't read it. Saving here writes it correctly."

/**
 * Decodes a datamap file: UTF-8 when it is valid UTF-8, otherwise Windows-1252, Excel's plain
 * "CSV". A UTF-8 BOM is dropped either way.
 */
export const decodeMapBytes = (bytes: Uint8Array): { text: string; encoding: "utf-8" | "windows-1252" } => {
  try {
    return { text: new TextDecoder("utf-8", { fatal: true }).decode(bytes), encoding: "utf-8" }
  } catch {
    const hasBom = bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf
    return { text: new TextDecoder("windows-1252").decode(hasBom ? bytes.subarray(3) : bytes), encoding: "windows-1252" }
  }
}

/** A file's bytes. FileReader rather than Blob.arrayBuffer, which jsdom's Blob lacks. */
const readBytes = (file: Blob): Promise<Uint8Array> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer))
    reader.onerror = () => reject(reader.error ?? new Error("The file could not be read."))
    reader.readAsArrayBuffer(file)
  })

/**
 * Why an uploaded datamap should go to the editor instead of the server, or null when it can be
 * uploaded as it is. The editor gets the text and the note.
 */
export const mapUploadProblem = async (file: Blob): Promise<{ text: string; note: string } | null> => {
  const { text, encoding } = decodeMapBytes(await readBytes(file))
  if (encoding === "windows-1252") return { text, note: NOT_UTF8_NOTE }

  const parsed = parseDatamapCsv(text)
  if (!parsed.ok) return { text, note: `This datamap was not uploaded. ${parsed.error}` }

  const issues = validateDatamap(parsed.doc)
  if (issues.length > 0)
    return {
      text,
      note: `This datamap was not uploaded: it has ${issues.length} problem${issues.length === 1 ? "" : "s"} that would stop rows importing. Fix ${issues.length === 1 ? "it" : "them"} below, then save to upload it.`,
    }
  if (!parsed.headerExact)
    return {
      text,
      note: "This datamap was not uploaded: its header row must be exactly LABEL,TYPE,VARIABLE,VALUES. Saving here writes it correctly.",
    }
  if (parsed.blankRows > 0)
    return {
      text,
      note: `This datamap was not uploaded: it has ${parsed.blankRows} empty row${parsed.blankRows === 1 ? "" : "s"}, which the import can't read. Saving here removes ${parsed.blankRows === 1 ? "it" : "them"}.`,
    }
  return null
}
